import { Visibility as ViewIcon } from '@mui/icons-material';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Box,
  Typography,
  Divider,
  Paper,
  Stack,
  Button,
} from '@mui/material';
import { entityType } from '@nohara-dxdev/mt-common';
import { permission } from '@nohara-dxdev/oidc-core';
import { OidcProtectedComponent, useScopeIds } from '@nohara-dxdev/react-oidc';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ChatInterface } from '@/features/comments/components/chat-interface';
import { TaskEntity } from '@/types/api';

import { useUpdateTask } from '../api';

import { TaskStatusChip } from './task-status-chip';

const readTaskPermissions = [permission.task.read];
const updateTaskPermissions = [permission.task.update];

export interface ViewTaskButtonProps {
  task: TaskEntity;
}

export const ViewTaskButton = ({ task }: ViewTaskButtonProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const updateTask = useUpdateTask(task.id);
  const scopeIds = useScopeIds();

  const subject = useMemo(
    () => ({
      ...scopeIds,
      taskId: task.id,
    }),
    [scopeIds, task.id],
  );

  const handleToggleStatus = async () => {
    const nextStatus = task.status === 'open' ? 'closed' : 'open';
    setUpdating(true);
    try {
      await updateTask.mutateAsync({ status: nextStatus });
    } finally {
      setUpdating(false);
    }
  };

  return (
    <OidcProtectedComponent requireAll={readTaskPermissions} subject={subject}>
      <Tooltip title={t('common.view', 'View')}>
        <IconButton
          size="small"
          onClick={() => {
            setOpen(true);
          }}
          color="primary"
        >
          <ViewIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
          >
            <Typography variant="h6" noWrap>
              {task.title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TaskStatusChip status={task.status} size="medium" />
              <OidcProtectedComponent requireAll={updateTaskPermissions} subject={subject}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => void handleToggleStatus()}
                  disabled={updating}
                >
                  {t(`tasks.status.toggleTo.${task.status === 'open' ? 'closed' : 'open'}`)}
                </Button>
              </OidcProtectedComponent>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2.5}>
            {task.description && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('tasks.create.descriptionLabel')}
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {task.description}
                  </Typography>
                </Paper>
              </Box>
            )}

            <Divider textAlign="left">
              <Typography variant="subtitle2" color="text.secondary">
                {t('comments.chat.title')}
              </Typography>
            </Divider>

            <Box>
              <ChatInterface entityId={task.id} entityType={entityType.task} />
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>
    </OidcProtectedComponent>
  );
};
